import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Badge } from "react-bootstrap";
import Loader from "../../components/common/Loader";
import theme from "../../styles/theme/theme";
import { timeAgo } from "../../utils/timeAgo";
import { capitalizeFirst } from "../../utils/stringUtils";

import {
  cancelOrder,
  fetchTransactionDetails,
  generateInvoice,
} from "../../features/buyer/buyerSlice";

import { FaMapMarkerAlt, FaDollarSign } from "react-icons/fa";

const TransactionDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    data: transactionDetail,
    loading,
    error,
  } = useSelector((state) => state.buyer.transactionDetails);

  useEffect(() => {
    if (id) {
      dispatch(fetchTransactionDetails(id))
        .unwrap()
        .catch((err) =>
          console.error("Failed to load transaction details:", err)
        );
    }
  }, [dispatch, id]);

  if (loading) return <Loader />;
  if (error) return <div className="text-danger">{error}</div>;
  if (!transactionDetail) return null;

  const {
    id: transactionId,
    final_price,
    status,
    quantity,
    created_at,
    buyer_product,
    seller,
    buyer,
  } = transactionDetail;

  const currency = buyer_product?.currency || "USD";

  const price = (value) => `${currency} ${Number(value || 0).toLocaleString()}`;

  return (
    <Container className="py-4 px-3 px-md-5">
      <h2 style={{ color: theme.palette.text.primary, fontWeight: 600 }}>
        Transaction Details
      </h2>

      {/* Transaction Summary */}
      <div style={sectionCard}>
        <Row>
          <Col md={4}>
            <div style={infoRow}>
              <strong>Transaction ID:</strong> #{transactionId}
            </div>
            <div style={infoRow}>
              <strong>Status:</strong>{" "}
              <Badge bg={statusColor(status)}>{capitalizeFirst(status)}</Badge>
            </div>
          </Col>
          <Col md={4}>
            <div style={infoRow}>
              <strong>Created:</strong> {timeAgo(created_at)}
            </div>
            <div style={infoRow}>
              <strong>Quantity:</strong> {quantity}{" "}
              {buyer_product.unit_of_measurement}
            </div>
          </Col>
          <Col md={4}>
            <div style={infoRow}>
              <strong>Final Payable:</strong>{" "}
              <span style={{ fontWeight: 600 }}>{price(final_price)}</span>
            </div>
          </Col>
        </Row>
      </div>

      {/* Product Summary */}
      <div style={sectionCard}>
        <Row>
          <Col md={4}>
            <img
              src={buyer_product.image_url}
              alt={buyer_product.name}
              loading="lazy"
              style={{
                width: "100%",
                borderRadius: 12,
                objectFit: "contain",
                height: 220,
              }}
            />
          </Col>
          <Col md={8}>
            <h4 style={sectionTitle}>{buyer_product.name}</h4>
            <div
              className="ql-editor"
              style={{ ...textMuted, padding: 0 }}
              dangerouslySetInnerHTML={{
                __html: buyer_product.description || "No description provided.",
              }}
            />

            <div style={infoRow}>
              <FaMapMarkerAlt size={14} color={theme.palette.primary.main} />
              <span style={{ marginLeft: 6 }}>{buyer_product.location}</span>
            </div>

            <div style={infoRow}>
              <strong>Sorting Type:</strong> {buyer_product.sorting_type}
            </div>

            <div style={infoRow}>
              <strong>Hazard:</strong>{" "}
              {buyer_product.hazard_types?.join(", ") || "N/A"}
            </div>
          </Col>
        </Row>
      </div>

      {/* Pricing Breakdown */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Pricing Breakdown</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Max Buying Price:</strong>{" "}
              {price(buyer_product.max_buying_price)}
            </div>
            <div style={infoRow}>
              <strong>Discount:</strong> {buyer_product.discount_percentage}%
            </div>
            <div style={infoRow}>
              <strong>Discounted Price:</strong>{" "}
              {price(buyer_product.discounted_price)}
            </div>
          </Col>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Price / Unit:</strong>{" "}
              {price(buyer_product.price_per_unit)}
            </div>
            <div style={infoRow}>
              <strong>Estimated Total:</strong>{" "}
              {price(buyer_product.estimated_price)}
            </div>
          </Col>
        </Row>
      </div>

      {/* Trade & Delivery */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Trade & Delivery</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Incoterms:</strong> {buyer_product.incoterms}
            </div>
            <div style={infoRow}>
              <strong>Payment Terms:</strong> {buyer_product.payment_terms}
            </div>
          </Col>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Lead Time:</strong> {buyer_product.lead_time} days
            </div>
            <div style={infoRow}>
              <strong>Delivery Port:</strong>{" "}
              {buyer_product.delivery_port || "N/A"}
            </div>
          </Col>
        </Row>
      </div>

      {/* Buyer Info */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Buyer Information</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Name:</strong> {buyer.name}
            </div>
            <div style={infoRow}>
              <strong>Email:</strong> {buyer.email}
            </div>
            <div style={infoRow}>
              <strong>Phone:</strong> {buyer.phone}
            </div>
            <div style={infoRow}>
              <strong>Company:</strong> {buyer.company_name}
            </div>
          </Col>
        </Row>
      </div>

      {/* Seller Info */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Seller Information</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Name:</strong> {seller.name}
            </div>
            <div style={infoRow}>
              <strong>Email:</strong> {seller.email}
            </div>
            <div style={infoRow}>
              <strong>Phone:</strong> {seller.phone}
            </div>
            <div style={infoRow}>
              <strong>Company:</strong> {seller.company_name}
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

// Styles
const sectionCard = {
  backgroundColor: "#fff",
  padding: 24,
  borderRadius: 16,
  boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
  marginBottom: 24,
};

const sectionTitle = {
  fontWeight: 600,
  marginBottom: 16,
};

const infoRow = {
  marginBottom: 10,
  fontSize: "0.95rem",
};

const textMuted = {
  fontSize: "0.9rem",
  color: "#666",
};

const statusColor = (status) => {
  switch (status) {
    case "accepted":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "danger";
    default:
      return "secondary";
  }
};

export default TransactionDetails;
